/* eslint-disable @typescript-eslint/naming-convention */
import { useCallback, useEffect, useRef, useState } from 'react';

import { callUiApi } from '../../../common/fetch.js';
import { isInBrowser } from '../../../common/utils/ssrUtils.js';
import {
  submitChatFeedbackPrivateMethod,
  submitChatFeedbackPublicMethod,
} from '../../../generated/api/uiApiMethods.js';
import { useAuth } from '../../../public/site/AuthProvider';
import { clearActiveChatSession, hasHumanAgentEverResponded, markHumanAgentResponded } from '../chatSession.js';
import { GenesysService, isMessageReceivedEvent } from '../types.js';
import { isHumanAgentMessage } from '../utils.js';
import { useGenesysLogger } from './useGenesysLogger.js';
import type { ChatFeedbackData, ElisaChatClientApi, GenesysCustomAttributes } from '../types.js';

interface UseGenesysChatConfig {
  deploymentId: string;
  serviceName?: string;
  enabled?: boolean;
  widgetCdnUrl?: string;
  environment?: string;
  region?: string;
  deploymentName?: string;
}

interface UseGenesysChatReturn {
  conversationId: string | null;
  isSurveyPending: boolean;
  isBotOnlySurvey: boolean;
  onSurveySubmit: (data: ChatFeedbackData) => void;
  onSurveyClose: () => void;
  updateCustomAttributes: (attributes: Partial<GenesysCustomAttributes>) => void;
  getSurveyConversationId: () => string;
  surveyAgentName: string | undefined;
}

const DEFAULT_CONFIG = {
  environment: 'prod-euc1',
  region: 'euc1',
  deploymentName: 'Elisa Online Chat',
  statusPollDelayMs: 1000,
} as const;

const CHAT_WIDGET_SCRIPT_ID = 'elisa-chat-widget-sdk';

export const getElisaChatClient = (): ElisaChatClientApi | undefined => {
  return (window as Window & { ElisaChatClient?: ElisaChatClientApi }).ElisaChatClient;
};

const loadChatWidgetScript = (src: string): Promise<void> => {
  return new Promise((resolve, reject) => {
    const existing = document.getElementById(CHAT_WIDGET_SCRIPT_ID) as HTMLScriptElement | null;
    if (existing) {
      if (existing.dataset.loaded === 'true') {
        resolve();
        return;
      }

      // If a previous load attempt errored, remove the dead element and retry fresh
      if (existing.dataset.errored === 'true') {
        existing.remove();
      } else {
        existing.addEventListener('load', () => resolve(), { once: true });
        existing.addEventListener('error', () => reject(new Error(`Failed to load chat widget script: ${src}`)), {
          once: true,
        });
        return;
      }
    }

    const script = document.createElement('script');
    script.id = CHAT_WIDGET_SCRIPT_ID;
    script.src = src;
    script.async = true;
    script.onload = () => {
      script.dataset.loaded = 'true';
      resolve();
    };
    script.onerror = () => {
      script.dataset.errored = 'true';
      reject(new Error(`Failed to load chat widget script: ${src}`));
    };

    document.head.appendChild(script);
  });
};

export const useGenesysChat = (config: UseGenesysChatConfig): UseGenesysChatReturn => {
  const {
    deploymentId,
    serviceName = GenesysService.OTHER,
    enabled = true,
    widgetCdnUrl,
    environment = DEFAULT_CONFIG.environment,
    region = DEFAULT_CONFIG.region,
    deploymentName = DEFAULT_CONFIG.deploymentName,
  } = config;

  const { log } = useGenesysLogger();
  const { isAuthenticated } = useAuth();

  const [conversationId, setConversationId] = useState<string | null>(null);
  const [isSurveyPending, setIsSurveyPending] = useState(false);
  const [isBotOnlySurvey, setIsBotOnlySurvey] = useState(false);
  const [surveyAgentName, setSurveyAgentName] = useState<string | undefined>(undefined);

  const widgetInitializedRef = useRef(false);
  const customAttributesRef = useRef<Partial<GenesysCustomAttributes>>({});
  const surveyMessageIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (!enabled || !widgetCdnUrl || !deploymentId || !isInBrowser()) {
      return;
    }

    let cancelled = false;
    let statusPoller: number | undefined;
    let unsubscribeSurvey: (() => void) | undefined;
    let unsubscribeMessages: (() => void) | undefined;

    const initialize = async (): Promise<void> => {
      try {
        await loadChatWidgetScript(widgetCdnUrl);
        if (cancelled) {
          return;
        }

        const client = getElisaChatClient();
        if (!client) {
          log('ElisaChatClient missing after script load');
          return;
        }

        if (!widgetInitializedRef.current) {
          client.init({
            deploymentId,
            deploymentEnv: environment,
            region,
            deploymentName,
            ui: {
              branding: {
                agentName: 'Elisa Apulainen',
              },
            },
            disableHostAuthProvider: true,
            disableSessionProvider: true,
            customSurveyUI: true,
            downloadTranscript: true,
            analyticsService: serviceName,
            customAttributes: () => ({ deploymentId, deploymentName, ...customAttributesRef.current }),
          });
          widgetInitializedRef.current = true;
        }

        // Subscribe to survey events — SURVEY_OPEN fires after user accepts
        // the widget's built-in toaster prompt ("Kuinka onnistuimme?")
        unsubscribeSurvey = client.onSurveyOpen(({ messageId, agentName }) => {
          surveyMessageIdRef.current = messageId;
          setIsSurveyPending(true);
          setIsBotOnlySurvey(!hasHumanAgentEverResponded());
          setSurveyAgentName(agentName);
          log('Survey accepted by user', { messageId, hasAgentName: !!agentName });
        });

        unsubscribeMessages = client.on(event => {
          if (!isMessageReceivedEvent(event)) {
            return;
          }

          if (isHumanAgentMessage(event.payload)) {
            markHumanAgentResponded();
          }
        });

        statusPoller = window.setInterval(() => {
          const state = client.getDebugState();
          setConversationId(prev => state.conversationId ?? prev);
        }, DEFAULT_CONFIG.statusPollDelayMs);
      } catch (error) {
        log('Custom widget init failed', error);
      }
    };

    initialize();

    return () => {
      cancelled = true;

      if (statusPoller) {
        window.clearInterval(statusPoller);
      }

      if (unsubscribeSurvey) {
        unsubscribeSurvey();
      }

      if (unsubscribeMessages) {
        unsubscribeMessages();
      }

      const client = getElisaChatClient();
      if (client && widgetInitializedRef.current) {
        client.destroy();
        widgetInitializedRef.current = false;
      }

      setConversationId(null);
    };
  }, [enabled, widgetCdnUrl, deploymentId, environment, region, deploymentName, serviceName, log]);

  const onSurveySubmit = useCallback(
    async (data: ChatFeedbackData) => {
      try {
        const submitChatFeedbackMethod = isAuthenticated
          ? submitChatFeedbackPrivateMethod
          : submitChatFeedbackPublicMethod;
        const response = await callUiApi(submitChatFeedbackMethod(), {
          body: JSON.stringify(data),
          useDefaultMdmId: false,
        });

        if (response.ok) {
          log('Survey submitted successfully', data);
          setConversationId(null);
          surveyMessageIdRef.current = null;
          setSurveyAgentName(undefined);

          const client = getElisaChatClient();
          if (client) {
            client.closeSurvey().catch(() => {});
          }
        } else {
          log('Survey submission failed', { status: response.status, statusText: response.statusText });
        }
      } catch (error) {
        log('Survey submission error', error);
      }
    },
    [isAuthenticated, log]
  );

  const getSurveyConversationId = useCallback((): string => {
    return surveyMessageIdRef.current || conversationId || '';
  }, [conversationId]);

  const onSurveyClose = useCallback(() => {
    setIsSurveyPending(false);
    setIsBotOnlySurvey(false);
    setConversationId(null);
    surveyMessageIdRef.current = null;
    setSurveyAgentName(undefined);

    const client = getElisaChatClient();
    if (client) {
      client.closeSurvey().catch(() => {});
    }

    clearActiveChatSession();

    log('Survey overlay closed');
  }, [log]);

  const updateCustomAttributes = useCallback(
    (attributes: Partial<GenesysCustomAttributes>) => {
      if (!enabled || !isInBrowser()) {
        return;
      }

      customAttributesRef.current = attributes;

      const client = getElisaChatClient();
      if (client) {
        client.refreshCustomAttributes().catch(() => {});
      }
    },
    [enabled]
  );

  return {
    conversationId,
    isSurveyPending,
    isBotOnlySurvey,
    onSurveySubmit,
    onSurveyClose,
    updateCustomAttributes,
    getSurveyConversationId,
    surveyAgentName,
  };
};
